import { append, compose, filter, is, map, reduce, path } from 'ramda'
import HttpCacheAbstractService from './HttpCacheAbstractService'
import HttpCacheItem from '../container/HttpCacheItem'
import service from '../dictionary/service.json'

const isArray = is(Array)
const pathFunc = path

/**
 * Cache service that only handles reading when an individual item is being loaded.
 *
 * This is the simplest implementation of the HTTP caching. Please use another service if your needs are more complex.
 */
export default class HttpCacheItemService extends HttpCacheAbstractService {
  /**
   * Constructor.
   *
   * @param {function} calculateExpiresOnValue
   * @param {function} getServiceArgsFunction
   */
  constructor(calculateExpiresOnValue, getServiceArgsFunction) {
    super(service.item)
    this.calculateExpiresOnValue = calculateExpiresOnValue
    this.getServiceArgsFunction = getServiceArgsFunction
  }

  /**
   * Reads the cache pool for matches for the current request.
   *
   * @param {string} path
   * @param {Object} parameters
   * @param {HttpCachePoolConfig} poolConfig
   * @param {string|null|undefined} itemType
   * @param {Array<HttpCacheItem>} cachePool
   * @return {HttpCacheItem|Array<HttpCacheItem>|null}
   */
  read({ path, parameters, poolConfig, itemType }, cachePool) {
    const id = this.getServiceArgsFunction(poolConfig, 'requestIdProvider')(path, parameters)
    const itemOrItems = compose(
      map(pathFunc(['value'])),
      filter((item) => item.id === id && item.type === (itemType || poolConfig.name)),
    )(cachePool)
    const { length } = itemOrItems

    if (length > 1) {
      return itemOrItems
    }
    if (length === 1) {
      return itemOrItems[0]
    }

    return null
  }

  /**
   * Writes to the cache pool.
   *
   * @param {string} path
   * @param {Object} parameters
   * @param {HttpCachePoolConfig} poolConfig
   * @param {string|null|undefined} itemType
   * @param {Number} currentTimestamp
   * @param {Array<HttpCacheItem>} cachePool
   * @param {*} valueOrValues
   * @return {Array<HttpCacheItem>}
   */
  write({ path, parameters, poolConfig, itemType, currentTimestamp }, cachePool, valueOrValues) {
    if (isArray(valueOrValues)) {
      return reduce(
        (cachePool, item) => this.write({ path, parameters, poolConfig, itemType, currentTimestamp }, cachePool, item),
        cachePool,
        valueOrValues,
      )
    }

    const id = this.getServiceArgsFunction(poolConfig, 'entityIdProvider')(valueOrValues, path, parameters)
    const expiresOn = this.calculateExpiresOnValue(poolConfig)

    return compose(
      append(new HttpCacheItem(currentTimestamp, expiresOn, itemType || poolConfig.name, id, valueOrValues)),
      filter((item) => (item.id !== id || item.type !== (itemType || poolConfig.name))),
    )(cachePool)
  }
}
